<template>
  <div class="bc-white padding-20 readme-page">
    <div class="display-flex jc-space-between ai-center mb-20">
      <b class="fs-18">说明书管理</b>
      <el-button type="primary" icon="el-icon-plus" size="small" @click="openCreate">新增说明书</el-button>
    </div>

    <el-form :inline="true" size="small" class="readme-search" @submit.native.prevent="search">
      <el-form-item>
        <el-input
          v-model="searchForm.fileKey"
          clearable
          placeholder="请输入关键字搜索"
          @input="searchOnInput"
          @keyup.enter.native="search"
        ></el-input>
      </el-form-item>
      <el-form-item>
        <el-button type="primary" icon="el-icon-search" :loading="loading" @click="search">查询</el-button>
        <el-button icon="el-icon-refresh-left" :loading="loading" @click="resetSearch">重置</el-button>
      </el-form-item>
    </el-form>

    <el-table v-loading="loading" :data="tableData" border stripe row-key="id" empty-text="暂无说明书">
      <el-table-column prop="fileName" label="说明书名称" min-width="220" show-overflow-tooltip></el-table-column>
      <el-table-column label="关键字" min-width="220">
        <template #default="{row}">
          <div class="readme-tags">
            <el-tag v-for="(tag, index) in splitKeywords(row.fileKey)" :key="`${row.id}-${index}`" size="mini" effect="plain">{{ tag }}</el-tag>
          </div>
        </template>
      </el-table-column>
      <el-table-column label="说明书状态" width="140">
        <template #default="{row}">
          <el-select
            :value="statusValue(row.fileStatus)"
            size="small"
            placeholder="未知"
            :disabled="statusUpdatingId !== ''"
            @change="changeStatus(row, $event)"
          >
            <el-option label="展示" :value="0"></el-option>
            <el-option label="不展示" :value="1"></el-option>
            <el-option label="废弃/归档" :value="2"></el-option>
          </el-select>
        </template>
      </el-table-column>
      <el-table-column label="文件" width="170">
        <template #default="{row}">
          <el-button type="text" icon="el-icon-view" @click="preview(row)">预览</el-button>
          <el-upload
            class="readme-replace-upload"
            action="#"
            accept=".pdf,application/pdf"
            :show-file-list="false"
            :disabled="replacingId !== ''"
            :http-request="replaceUpload(row)"
            :before-upload="beforeUpload"
          >
            <el-button type="text" icon="el-icon-upload2" :loading="replacingId === row.id">更换文件</el-button>
          </el-upload>
        </template>
      </el-table-column>
      <el-table-column label="操作" width="150" fixed="right">
        <template #default="{row}">
          <div class="readme-actions">
            <el-button type="text" icon="el-icon-edit-outline" @click="openEdit(row)">编辑</el-button>
            <el-button
              type="text"
              class="color-danger"
              icon="el-icon-delete"
              :loading="deletingId === row.id"
              :disabled="deletingId !== '' && deletingId !== row.id"
              @click="remove(row)"
            >删除</el-button>
          </div>
        </template>
      </el-table-column>
    </el-table>

    <div class="display-flex fd-row-reverse mt-20">
      <el-pagination
        hide-on-single-page
        :current-page="searchForm.pageNum"
        :page-size="searchForm.pageSize"
        :page-sizes="[10, 20, 50]"
        layout="total, sizes, prev, pager, next, jumper"
        :total="total"
        @current-change="loadList"
        @size-change="changePageSize"
      ></el-pagination>
    </div>

    <el-dialog
      :title="editingId ? '编辑说明书' : '新增说明书'"
      :visible.sync="formVisible"
      width="560px"
      append-to-body
      @closed="resetForm"
    >
      <el-form label-width="100px" size="small">
        <el-form-item label="说明书文件" required>
          <el-upload
            class="readme-upload"
            action="#"
            accept=".pdf,application/pdf"
            drag
            :show-file-list="false"
            :disabled="uploading"
            :http-request="uploadFile"
            :before-upload="beforeUpload"
          >
            <i class="el-icon-upload"></i>
            <div class="el-upload__text">将 PDF 文件拖到此处，或<em>点击上传</em></div>
          </el-upload>
          <div v-if="form.fileUrl" class="readme-uploaded-file">已上传：{{ uploadedFileName || form.fileName || form.fileUrl }}</div>
        </el-form-item>
        <el-form-item label="说明书名称" required>
          <el-input v-model="form.fileName" clearable placeholder="请输入说明书名称"></el-input>
        </el-form-item>
        <el-form-item label="关键字">
          <div class="readme-keyword-tags">
            <el-tag
              v-for="tag in keywordTags"
              :key="tag"
              closable
              :disable-transitions="false"
              @close="removeKeywordTag(tag)"
            >{{ tag }}</el-tag>
            <el-input
              ref="keywordInput"
              v-model="keywordInputValue"
              class="readme-keyword-input"
              size="small"
              placeholder="输入后按回车"
              @keyup.enter.native.prevent="addKeywordTag"
              @blur="addKeywordTag"
            ></el-input>
          </div>
        </el-form-item>
        <el-form-item label="说明书状态" required>
          <el-radio-group v-model="form.fileStatus">
            <el-radio :label="0">展示</el-radio>
            <el-radio :label="1">不展示</el-radio>
            <el-radio :label="2">废弃/归档</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="版本号">
          <el-input v-model="form.fileVersion" clearable placeholder="请输入版本号"></el-input>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button size="small" @click="formVisible = false">取消</el-button>
        <el-button type="primary" size="small" :loading="saving" :disabled="uploading" @click="save">保存</el-button>
      </span>
    </el-dialog>

    <el-dialog
      :title="previewTitle"
      :visible.sync="previewVisible"
      width="78%"
      top="4vh"
      append-to-body
      @closed="previewUrl = ''"
    >
      <iframe v-if="previewUrl" class="readme-preview" :src="previewUrl" title="说明书预览"></iframe>
      <el-empty v-else description="暂无可预览内容"></el-empty>
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: 'ReadmeManagement',
  data () {
    return {
      loading: false,
      tableData: [],
      total: 0,
      searchForm: { fileKey: '', pageNum: 1, pageSize: 10 },
      formVisible: false,
      saving: false,
      uploading: false,
      deletingId: '',
      replacingId: '',
      statusUpdatingId: '',
      editingId: '',
      form: { fileName: '', fileUrl: '', fileStatus: 0, fileVersion: '' },
      uploadedFileName: '',
      keywordTags: [],
      keywordInputValue: '',
      previewVisible: false,
      previewTitle: '',
      previewUrl: '',
      searchTimer: null,
      loadRequestId: 0
    }
  },
  mounted () {
    this.loadList(1)
  },
  beforeDestroy () {
    clearTimeout(this.searchTimer)
  },
  methods: {
    searchOnInput () {
      clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => this.loadList(1), 300)
    },
    search () {
      clearTimeout(this.searchTimer)
      this.loadList(1)
    },
    async loadList (page = this.searchForm.pageNum) {
      const requestId = ++this.loadRequestId
      this.searchForm.pageNum = page
      this.loading = true
      try {
        const result = await this.$api.instruction.list({
          pageNum: this.searchForm.pageNum,
          pageSize: this.searchForm.pageSize,
          fileKey: this.searchForm.fileKey.trim()
        }) || {}
        if (requestId !== this.loadRequestId) return
        this.tableData = Array.isArray(result.list) ? result.list : []
        this.total = Number(result.total) || 0
      } catch (error) {
        if (requestId !== this.loadRequestId) return
        this.tableData = []
        this.total = 0
        this.$message.error(error.message || '说明书查询失败')
      } finally {
        if (requestId === this.loadRequestId) this.loading = false
      }
    },
    resetSearch () {
      clearTimeout(this.searchTimer)
      this.searchForm.fileKey = ''
      this.loadList(1)
    },
    changePageSize (size) {
      this.searchForm.pageSize = size
      this.loadList(1)
    },
    splitKeywords (value) {
      return String(value || '').split(/[、,，]/).map(item => item.trim()).filter(Boolean)
    },
    statusValue (status) {
      return [0, 1, 2].includes(status) ? status : undefined
    },
    statusLabel (status) {
      return status === 0 ? '展示' : status === 1 ? '不展示' : '废弃/归档'
    },
    openCreate () {
      this.editingId = ''
      this.uploadedFileName = ''
      this.keywordTags = []
      this.keywordInputValue = ''
      this.formVisible = true
    },
    openEdit (row) {
      this.editingId = row.id
      this.form = {
        fileName: this.fileNameWithoutExtension(row.fileName),
        fileUrl: row.fileUrl,
        fileStatus: this.statusValue(row.fileStatus) === undefined ? 0 : row.fileStatus,
        fileVersion: row.fileVersion || ''
      }
      this.uploadedFileName = row.fileName || ''
      this.keywordTags = this.splitKeywords(row.fileKey)
      this.formVisible = true
    },
    resetForm () {
      this.editingId = ''
      this.form = { fileName: '', fileUrl: '', fileStatus: 0, fileVersion: '' }
      this.uploadedFileName = ''
      this.keywordTags = []
      this.keywordInputValue = ''
    },
    addKeywordTag () {
      const tags = this.keywordInputValue.split(/[、,，]/).map(tag => tag.trim()).filter(Boolean)
      tags.forEach(tag => {
        if (!this.keywordTags.includes(tag)) this.keywordTags.push(tag)
      })
      this.keywordInputValue = ''
    },
    removeKeywordTag (tag) {
      this.keywordTags = this.keywordTags.filter(item => item !== tag)
    },
    fileNameWithoutExtension (value) {
      const name = String(value || '').trim()
      const index = name.lastIndexOf('.')
      return index > 0 ? name.slice(0, index) : name
    },
    beforeUpload (file) {
      if (file.type !== 'application/pdf' && !/\.pdf$/i.test(file.name)) {
        this.$message.warning('请上传 PDF 文件')
        return false
      }
      return true
    },
    async uploadFile (options) {
      this.uploading = true
      try {
        const result = await this.$api.instruction.upload(options.file)
        this.form.fileUrl = result.key
        this.uploadedFileName = options.file.name
        if (!this.form.fileName.trim()) this.form.fileName = this.fileNameWithoutExtension(options.file.name)
        options.onSuccess(result)
        this.$message.success('文件上传成功')
      } catch (error) {
        options.onError(error)
        this.$message.error(error.message || '文件上传失败')
      } finally {
        this.uploading = false
      }
    },
    replaceUpload (row) {
      return async (options) => {
        this.replacingId = row.id
        try {
          const result = await this.$api.instruction.upload(options.file)
          await this.$api.instruction.update({
            id: row.id,
            fileName: row.fileName,
            fileUrl: result.key,
            fileKey: row.fileKey || '',
            fileStatus: row.fileStatus === undefined ? 0 : row.fileStatus,
            fileVersion: row.fileVersion || ''
          })
          options.onSuccess(result)
          this.$message.success('文件更换成功')
          await this.loadList()
        } catch (error) {
          options.onError(error)
          this.$message.error(error.message || '文件更换失败')
        } finally {
          this.replacingId = ''
        }
      }
    },
    async save () {
      if (!this.form.fileUrl) return this.$message.warning('请先上传说明书文件')
      if (!this.form.fileName.trim()) return this.$message.warning('请输入说明书名称')
      if (![0, 1, 2].includes(this.form.fileStatus)) return this.$message.warning('请选择说明书状态')
      this.saving = true
      const data = {
        fileName: this.form.fileName.trim(),
        fileUrl: this.form.fileUrl,
        fileKey: this.keywordTags.join('、'),
        fileStatus: this.form.fileStatus,
        fileVersion: this.form.fileVersion.trim()
      }
      try {
        if (this.editingId) await this.$api.instruction.update({ ...data, id: this.editingId })
        else await this.$api.instruction.save(data)
        this.$message.success(this.editingId ? '说明书更新成功' : '说明书新增成功')
        this.formVisible = false
        await this.loadList()
      } catch (error) {
        this.$message.error(error.message || '说明书保存失败')
      } finally {
        this.saving = false
      }
    },
    async remove (row) {
      try {
        await this.$confirm(`确定删除说明书“${row.fileName}”吗？删除后不可恢复。`, '删除说明书', {
          confirmButtonText: '确定删除',
          cancelButtonText: '取消',
          type: 'warning'
        })
      } catch (error) {
        return error
      }
      this.deletingId = row.id
      try {
        await this.$api.instruction.remove(row.id)
        this.$message.success('删除成功')
        await this.loadList(this.tableData.length === 1 && this.searchForm.pageNum > 1 ? this.searchForm.pageNum - 1 : this.searchForm.pageNum)
      } catch (error) {
        this.$message.error(error.message || '说明书删除失败')
      } finally {
        this.deletingId = ''
      }
    },
    async changeStatus (row, nextStatus) {
      if (row.fileStatus === nextStatus) return
      try {
        await this.$confirm(`确定将“${row.fileName}”状态修改为“${this.statusLabel(nextStatus)}”吗？`, '修改说明书状态', {
          confirmButtonText: '确定修改',
          cancelButtonText: '取消',
          type: 'warning'
        })
      } catch (error) {
        return error
      }
      this.statusUpdatingId = row.id
      try {
        await this.$api.instruction.update({
          id: row.id,
          fileName: row.fileName,
          fileUrl: row.fileUrl,
          fileKey: row.fileKey || '',
          fileStatus: nextStatus,
          fileVersion: row.fileVersion || ''
        })
        this.$set(row, 'fileStatus', nextStatus)
        this.$message.success(`状态已修改为${this.statusLabel(nextStatus)}`)
      } catch (error) {
        this.$message.error(error.message || '说明书状态更新失败')
      } finally {
        this.statusUpdatingId = ''
      }
    },
    preview (row) {
      if (!row.filePreviewUrl) return this.$message.warning('当前说明书没有可预览地址')
      this.previewTitle = row.fileName || '说明书预览'
      this.previewUrl = row.filePreviewUrl
      this.previewVisible = true
    }
  }
}
</script>

<style lang="scss" scoped>
.readme-page {
  min-height: 360px;
}

.readme-search {
  margin-bottom: 18px;
}

.readme-search .el-form-item {
  margin-bottom: 0;
}

.readme-search .el-input {
  width: 260px;
}

.readme-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
}

.readme-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.readme-keyword-tags {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.readme-keyword-input {
  width: 120px;
}

.readme-replace-upload {
  display: inline-block;
  margin-left: 8px;
}

.readme-upload {
  width: 100%;
}

.readme-uploaded-file {
  margin-top: 8px;
  color: #909399;
  font-size: 12px;
  overflow-wrap: anywhere;
}

.readme-preview {
  width: 100%;
  height: 75vh;
  border: 0;
}
</style>
